import { History, Cloud, Trash2, HardDrive } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Link } from "react-router-dom"
import { NAVIGATION_ROUTES } from '@/constants/routes'
import { useState, useEffect } from "react"

interface DashboardSidebarProps {
  activePage?: "history" | "cloud" | "trash"
}

interface StorageQuota {
  totalQuota: number
  usedStorage: number
  freeStorage: number
  usagePercent: number
  totalQuotaHuman: string
  usedStorageHuman: string
  freeStorageHuman: string
  totalFiles: number
  totalShares: number
  activeShares: number
}

export function DashboardSidebar({ activePage = "history" }: DashboardSidebarProps) {
  const [quota, setQuota] = useState<StorageQuota | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // 只在组件挂载时获取一次容量信息，不再使用定时轮询
    fetchStorageQuota()
  }, [])

  const fetchStorageQuota = async () => {
    try {
      const token = localStorage.getItem('accessToken')
      console.log('[StorageQuota] 开始获取存储容量，token存在:', !!token)
      
      if (!token) {
        console.log('[StorageQuota] 未找到accessToken，用户未登录')
        setLoading(false)
        return
      }

      console.log('[StorageQuota] 调用API: /api/v1/profile/storage-quota')
      const response = await fetch('/api/v1/profile/storage-quota', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      console.log('[StorageQuota] API响应状态:', response.status)
      
      if (response.ok) {
        const data = await response.json()
        // console.log('[StorageQuota] 成功获取数据:', {
        //   totalQuota: data.data.totalQuotaHuman,
        //   usedStorage: data.data.usedStorageHuman,
        //   usagePercent: data.data.usagePercent,
        //   totalFiles: data.data.totalFiles
        // })
        setQuota(data.data)
      } else {
        const errorText = await response.text()
        console.error('[StorageQuota] API请求失败:', response.status, response.statusText)
        console.error('[StorageQuota] 错误详情:', errorText)
      }
    } catch (error) {
      console.error('[StorageQuota] 异常:', error)
    } finally {
      setLoading(false)
    }
  }

  const getProgressColor = (percent: number) => {
    if (percent < 50) return "bg-green-500"
    if (percent < 80) return "bg-yellow-500"
    return "bg-red-500"
  }

  return (
    <aside className="w-48 bg-gray-50 border-r border-gray-200 flex flex-col h-screen">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200">
        <Link to={NAVIGATION_ROUTES.HOME} className="flex items-center gap-2">
          <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white rounded-full" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">Easy Share</div>
            <div className="text-xs text-gray-600">安全、快速、便捷的文件分享</div>
          </div>
        </Link>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 p-4 space-y-2">
        <Link to={NAVIGATION_ROUTES.HISTORY}>
          <Button
            variant="ghost"
            className={`w-full justify-start ${activePage === "history" ? "text-gray-900 bg-white" : "text-gray-600"}`}
          >
            <History className="w-4 h-4 mr-3" />
            传输历史
          </Button>
        </Link>
        <Link to={NAVIGATION_ROUTES.CLOUD}>
          <Button
            variant="ghost"
            className={`w-full justify-start ${activePage === "cloud" ? "text-gray-900 bg-white" : "text-gray-600"}`}
          >
            <Cloud className="w-4 h-4 mr-3" />
            我的云盘
          </Button>
        </Link>
        <Link to={NAVIGATION_ROUTES.TRASH}>
          <Button
            variant="ghost"
            className={`w-full justify-start ${activePage === "trash" ? "text-gray-900 bg-white" : "text-gray-600"}`}
          >
            <Trash2 className="w-4 h-4 mr-3" />
            回收站
          </Button>
        </Link>
      </nav>

      {/* 底部容量显示 */}
      <div className="p-4 border-t border-gray-200 space-y-3">
        {loading ? (
          <div className="text-xs text-gray-500 text-center">加载中...</div>
        ) : quota ? (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600 flex items-center gap-1">
                  <HardDrive className="w-3 h-3" />
                  存储容量
                </span>
                <button 
                  onClick={fetchStorageQuota}
                  className="text-blue-600 hover:text-blue-700"
                  title="刷新"
                >
                  🔄
                </button>
              </div>
              
              {/* 进度条 */}
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${getProgressColor(quota.usagePercent)}`}
                  style={{ width: `${Math.min(quota.usagePercent, 100)}%` }}
                />
              </div>
              
              {/* 容量数字 */}
              <div className="font-medium text-gray-900 text-xs">
                {quota.usedStorageHuman} <span className="text-gray-500">/ {quota.totalQuotaHuman}</span>
              </div>
              
              {/* 使用百分比 */}
              {/* <div className="text-xs text-gray-500">
                已使用 {quota.usagePercent.toFixed(1)}%
              </div> */}
              
              {/* 统计信息 */}
             {/*  <div className="text-xs text-gray-600 space-y-1 pt-2 border-t border-gray-200">
                <div className="flex justify-between">
                  <span>📁 文件</span>
                  <span className="font-medium">{quota.totalFiles}</span>
                </div>
                <div className="flex justify-between">
                  <span>📤 分享</span>
                  <span className="font-medium">{quota.totalShares}</span>
                </div>
                <div className="flex justify-between">
                  <span>✅ 活跃</span>
                  <span className="font-medium text-green-600">{quota.activeShares}</span>
                </div>
              </div> */}
            </div>
          </>
        ) : (
          <div className="text-xs text-gray-500 text-center">
            未登录
          </div>
        )}
        
        {quota && quota.usagePercent > 80 && (
          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
            ⚠️ 空间不足
          </div>
        )}
        
        <Button className="w-full h-9 bg-black hover:bg-gray-800 text-white rounded-lg text-sm">
          升级容量
        </Button>
      </div>
    </aside>
  )
}
